import * as React from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TableSortLabel from "@mui/material/TableSortLabel";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import { visuallyHidden } from "@mui/utils";
import { useState } from "react";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import ClearIcon from "@mui/icons-material/Clear";
import SelectRole from "./RoleSelect";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import { GlobalMessageContext } from "../app/App";
import Tooltip from "@mui/material/Tooltip";

import PersonRemoveAlt1Icon from "@mui/icons-material/PersonRemoveAlt1";
import NewUser from "./NewUser";
import DeleteDialog from "../common/DeleteDialog";

import ConfigData from "../config/config.json";

const webserver = ConfigData.WEBSERVER;
/*
const putRole = (userName, role) => {
  fetch(webserver.protocol + '://' + webserver.host + ':' + webserver.port + '/user/setRole/' + userName + '?role=' + role, { method: 'PUT', credentials: 'include' })
      .then((response) => {
        if (response.status !== 200) {
          throw new Error('Error: ' + response.status);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        
      })
      .catch((error) => {
        //TODO global error cannot load user table
      });
};*/

const SearchBar = ({ searchQuery, setSearchQuery }) => (
  <Box sx={{ display: "flex" }} mb={2}>
    <TextField
      id="search-bar"
      className="text"
      value={searchQuery}
      onInput={(e) => {
        setSearchQuery(e.target.value);
      }}
      variant="outlined"
      placeholder="Suchen ..."
      size="small"
      sx={{ width: "100%" }}
      InputProps={{
        endAdornment: (
          <IconButton
            aria-label="search"
            onClick={(e) => {
              setSearchQuery("");
              document.getElementById("search-bar").focus();
            }}
          >
            {searchQuery ? <ClearIcon /> : <SearchIcon />}
          </IconButton>
        ),
      }}
    />
  </Box>
);

function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

// This method is created for cross-browser compatibility, if you don't
// need to support IE11, you can use Array.prototype.sort() directly
function stableSort(array, comparator) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) {
      return order;
    }
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

const headCells = [
  {
    id: "userName",
    numeric: false,
    disablePadding: false,
    label: "Benutzername",
  },
  {
    id: "email",
    numeric: false,
    disablePadding: false,
    label: "E-Mail",
  },
  {
    id: "role",
    numeric: false,
    disablePadding: false,
    label: "Rolle",
  },
];

function EnhancedTableHead(props) {
  const [open, setOpen] = React.useState(false);
  const { order, orderBy, onRequestSort } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {headCells.map((headCell) => (
          <TableCell
            key={headCell.id}
            align={headCell.numeric ? "right" : "left"}
            padding={headCell.disablePadding ? "none" : "normal"}
            sortDirection={orderBy === headCell.id ? order : false}
          >
            <TableSortLabel
              active={orderBy === headCell.id}
              direction={orderBy === headCell.id ? order : "asc"}
              onClick={createSortHandler(headCell.id)}
            >
              {headCell.label}
              {orderBy === headCell.id ? (
                <Box component="span" sx={visuallyHidden}>
                  {order === "desc" ? "sorted descending" : "sorted ascending"}
                </Box>
              ) : null}
            </TableSortLabel>
          </TableCell>
        ))}
        <TableCell align="right">
            <Tooltip title={"Benutzer hinzufügen"} enterDelay={1000}>
              <IconButton
                onClick={() => setOpen(true)}
                sx={{ color: "#00aa00", float: "right" }}
              >
                <PersonAddAlt1Icon />
              </IconButton>
            </Tooltip>
        </TableCell>
        <NewUser
          open={open}
          setOpen={setOpen}
          setRows={props.setRows}
          rows={props.rows}
        />
      </TableRow>
    </TableHead>
  );
}

EnhancedTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.oneOf(["asc", "desc"]).isRequired,
  orderBy: PropTypes.string.isRequired,
};

export default function EnhancedTable() {
  const { setGlobalMessage } = React.useContext(GlobalMessageContext);
  const [order, setOrder] = React.useState("asc");
  const [orderBy, setOrderBy] = React.useState("userName");
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(5);
  const [searchQuery, setSearchQuery] = useState("");

  const [rows, setRows] = useState([]);

  React.useEffect(() => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/user/getAll",
      { method: "GET", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then((response) => response.json())
      .then((data) => {
        for (var i = 0; i < data.length; i++) {
          data[i].deleteOpen = false;
        }

        setRows(data);
      })
      .catch((error) => {
        //TODO global error cannot load user table
      });
  }, []);

  const deleteUser = (index, userName) => {
    fetch(
      webserver.protocol +
        "://" +
        webserver.host +
        ":" +
        webserver.port +
        "/user/delete/" +
        userName,
      { method: "DELETE", credentials: "include" }
    )
      .then((response) => {
        if (response.status !== 200) {
          throw new Error("Error: " + response.status);
        }
        return response;
      })
      .then(() => {
        setRows((prev) => {
          let rowsCopy = JSON.parse(JSON.stringify(prev));
          rowsCopy.splice(index, 1);
          setGlobalMessage({
            globalMessage: "Benutzer gelöscht",
            severity: "success",
          });
          return rowsCopy;
        });
      })
      .catch((error) => {
        setGlobalMessage({
          globalMessage: "Vorgang fehlgeschlagen",
          severity: "error",
        });
      });
  };

  function queryUserRole(role) {
    if (role === "BLAK_EMPLOYEE") {
      return role.toString().toLowerCase().includes(searchQuery.toLowerCase());
    } else if (role === "EXAMINER") {
      return "prüfer".includes(searchQuery.toLowerCase());
    } else {
      return false;
    }
  }

  const filteredRows = rows.filter((row) => {
    return (
      row.userName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      row.email.toString().toLowerCase().includes(searchQuery.toLowerCase()) ||
      queryUserRole(row.role)
    );
  });

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    page > 0 ? Math.max(0, (1 + page) * rowsPerPage - filteredRows.length) : 0;

  return (
    <Box sx={{ width: "100%" }}>
      <SearchBar searchQuery={searchQuery} setSearchQuery={setSearchQuery} />
      <Paper sx={{ width: "100%", mb: 2 }}>
        <TableContainer>
          <Table
            sx={{ minWidth: 750 }}
            aria-labelledby="tableTitle"
            size={"medium"}
          >
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              setRows={setRows}
              rows={rows}
            />
            <TableBody>
              {/* if you don't need to support IE11, you can replace the `stableSort` call with:
                 rows.slice().sort(getComparator(order, orderBy)) */}
              {stableSort(filteredRows, getComparator(order, orderBy))
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row, pageIndex) => {
                  let index = 0;

                  for (let found = 0; found < rows.length; found++) {
                    if (rows[found].userName === row.userName) {
                      index = found;
                      break;
                    }
                  }
                  const labelId = `enhanced-table-checkbox-${index}`;

                  return (
                    <TableRow hover tabIndex={-1} key={row.userName}>
                      <TableCell id={labelId} scope="row">
                        {row.userName}
                      </TableCell>
                      <TableCell align="left">{row.email}</TableCell>
                      <TableCell align="left" padding="none">
                        {" "}
                        <SelectRole
                          name={row.userName}
                          index={index}
                          role={row.role}
                          setRole={setRows}
                        />{" "}
                      </TableCell>
                      <TableCell align="right">
                          <Tooltip title={"Benutzer entfernen"} enterDelay={1000}>
                            <IconButton
                              onClick={() =>
                                setRows((prev) => {
                                  let temp = JSON.parse(JSON.stringify(prev));
                                  temp[index].deleteOpen = true;
                                  return temp;
                                })
                              }
                            >
                              <PersonRemoveAlt1Icon sx={{ color: "#b50202" }} />
                            </IconButton>
                          </Tooltip>
                      </TableCell>
                      <DeleteDialog
                        function={() => deleteUser(index, row.userName)}
                        open={
                          row.deleteOpen !== undefined ? row.deleteOpen : false
                        }
                        setOpen={() =>
                          setRows((prev) => {
                            let temp = JSON.parse(JSON.stringify(prev));
                            temp[index].deleteOpen = false;
                            return temp;
                          })
                        }
                        title="Benutzer löschen"
                        text={
                          'Sind Sie sicher, dass Sie den Benutzer "' +
                          row.userName +
                          '" unwiderruflich löschen wollen?'
                        }
                      />
                    </TableRow>
                  );
                })}
              {emptyRows > 0 && (
                <TableRow
                  style={{
                    height: 73 * emptyRows,
                  }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelDisplayedRows={({ from, to, count }) => {
            return "" + from + "-" + to + " von " + count;
          }}
          labelRowsPerPage="Zeilen pro Seite"
        />
      </Paper>
    </Box>
  );
}
